/**
 * Utility for handling authenticated Server-Sent Events (SSE)
 * Uses the Initial Request with Token approach
 */

import React from 'react'

export class AuthenticatedSSE {
  constructor(url, options = {}) {
    this.url = url
    this.method = options.method || 'GET'
    this.headers = options.headers || {}
    this.body = options.body || null
    this.onMessage = options.onMessage || (() => {})
    this.onError = options.onError || (() => {})
    this.onClose = options.onClose || (() => {})
    this.reconnect = options.reconnect || false
    this.reconnectDelay = options.reconnectDelay || 5000
    this.abortController = null
  }

  async connect(token) {
    try {
      this.abortController = new AbortController()

      const headers = {
        ...this.headers,
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
      }

      // Add JWT token if provided
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const requestOptions = {
        method: this.method,
        headers,
        signal: this.abortController.signal,
      }

      // Add body for POST requests
      if (this.body && this.method !== 'GET') {
        requestOptions.body = this.body
      }

      const response = await fetch(this.url, requestOptions)

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`)
      }

      // Verify we got an event stream
      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('text/event-stream')) {
        throw new Error('Response is not an event stream')
      }

      // Start reading the stream
      await this.readStream(response.body)
    } catch (error) {
      if (error.name === 'AbortError') {
        this.onClose('Connection aborted')
      } else {
        this.onError(error)
        if (this.reconnect && !this.abortController.signal.aborted) {
          setTimeout(() => this.connect(token), this.reconnectDelay)
        }
      }
    }
  }

  async readStream(body) {
    const reader = body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      let done = false
      while (!done) {
        const result = await reader.read()
        done = result.done
        const value = result.value

        if (done) {
          this.onClose('Stream ended')
          break
        }

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.trim() === '') continue

          if (line.startsWith('data: ')) {
            const data = line.slice(6)

            if (data === '[DONE]') {
              this.onClose('Stream completed')
              return
            }

            try {
              const parsed = JSON.parse(data)
              this.onMessage(parsed)
            } catch (e) {
              // Handle non-JSON data
              this.onMessage({ type: 'text', content: data })
            }
          } else if (line.startsWith('event: ')) {
            // Handle custom events if needed
            // const event = line.slice(7)
            // Can be extended to handle different event types
          } else if (line.startsWith('id: ') || line.startsWith('retry: ')) {
            // Handle id and retry fields if needed
          }
        }
      }
    } catch (error) {
      this.onError(error)
    } finally {
      reader.releaseLock()
    }
  }

  disconnect() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }
}

/**
 * React hook for using AuthenticatedSSE
 */
export function useAuthenticatedSSE(url, options = {}) {
  const sseRef = React.useRef(null)

  React.useEffect(
    () => () => {
      if (sseRef.current) {
        sseRef.current.disconnect()
      }
    },
    []
  )

  const connect = React.useCallback(
    (token) => {
      if (sseRef.current) {
        sseRef.current.disconnect()
      }

      sseRef.current = new AuthenticatedSSE(url, options)
      return sseRef.current.connect(token)
    },
    [url, options]
  )

  const disconnect = React.useCallback(() => {
    if (sseRef.current) {
      sseRef.current.disconnect()
      sseRef.current = null
    }
  }, [])

  return { connect, disconnect }
}

/**
 * Helper function for simple SSE requests
 */
export async function streamSSE(url, token, options = {}) {
  return new Promise((resolve, reject) => {
    const messages = []

    const sse = new AuthenticatedSSE(url, {
      ...options,
      onMessage: (data) => {
        messages.push(data)
        if (options.onMessage) {
          options.onMessage(data)
        }
      },
      onError: (error) => {
        if (options.onError) {
          options.onError(error)
        }
        reject(error)
      },
      onClose: (reason) => {
        if (options.onClose) {
          options.onClose(reason)
        }
        resolve(messages)
      },
    })

    sse.connect(token)
  })
}
